'use client';

import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog';
import { Image as ImageType } from '@/types/types';

interface VideoTestimonialCardProps {
	testimonial_text: string;
	name: string;
	company_name: string;
	testimonial_video: ImageType;
}

export const VideoTestimonialCard = ({
	testimonial_text,
	name,
	company_name,
	testimonial_video,
}: VideoTestimonialCardProps) => {
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [isTextTruncated, setIsTextTruncated] = useState(false);
	const testimonialRef = useRef<HTMLParagraphElement>(null);
	// Check if text is truncated on mount and resize
	const checkTextTruncation = () => {
		const element = testimonialRef.current;
		if (element) {
			setIsTextTruncated(
				element.scrollHeight > element.clientHeight ||
					element.scrollWidth > element.clientWidth,
			);
		}
	};

	useEffect(() => {
		checkTextTruncation();

		// Add resize listener
		window.addEventListener('resize', checkTextTruncation);
		return () => window.removeEventListener('resize', checkTextTruncation);
	}, [testimonial_text]);

	return (
		<div role="article" className="mx-auto rounded-lg border">
			<div className="flex h-full flex-col rounded-lg bg-white p-6 shadow-lg">
				{/* Header Section - Fixed Height */}
				<div className="h-[140px] sm:h-[160px]">
					{/* Quote SVG */}
					<div className="mb-2 flex-shrink-0">
						<svg
							className="h-8 w-8 text-gray-400"
							fill="currentColor"
							viewBox="0 0 24 24"
							xmlns="http://www.w3.org/2000/svg">
							<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
						</svg>
					</div>

					{/* Testimonial Text with truncation */}
					<div className="relative">
						<p
							ref={testimonialRef}
							className="line-clamp-3 text-base text-gray-600 sm:text-lg">
							{testimonial_text}
						</p>
						{isTextTruncated && (
							<Button
								variant="ghost"
								size="sm"
								className="-ml-2 mb-1 px-2 text-blue-600 hover:text-blue-700"
								onClick={() => setIsDialogOpen(true)}>
								Read More
							</Button>
						)}
					</div>
				</div>

				{/* Video Section */}
				{testimonial_video?.url && (
					<div className="mb-6 mt-2 overflow-hidden rounded-lg">
						<div className="relative pt-[56.25%]">
							<video
								className="absolute inset-0 h-full w-full object-cover"
								controls
								src={testimonial_video.url}
								preload="metadata"
								data-testid="testimonial-video"
							/>
						</div>
					</div>
				)}

				{/* Name and Company */}
				<div className="mt-auto">
					<h3 className="text-lg font-semibold text-gray-900">
						{name}
					</h3>
					<p className="text-gray-500">{company_name}</p>
				</div>
			</div>

			{/* Full Testimonial Dialog */}

			<Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
				<DialogContent className="max-w-[90vw] sm:max-w-[600px] md:max-w-[800px]">
					<DialogHeader>
						<DialogTitle>{name}'s Testimonial</DialogTitle>
					</DialogHeader>
					<div className="mt-4 grid gap-6">
						<div className="flex items-start gap-4">
							<svg
								className="h-8 w-8 flex-shrink-0 text-gray-400"
								fill="currentColor"
								viewBox="0 0 24 24"
								xmlns="http://www.w3.org/2000/svg">
								<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
							</svg>
							<p className="text-gray-600">{testimonial_text}</p>
						</div>
						{testimonial_video?.url && (
							<div className="overflow-hidden rounded-lg">
								<div className="relative pt-[56.25%]">
									<video
										className="absolute inset-0 h-full w-full object-cover"
										controls
										src={testimonial_video.url}
										data-testid="testimonial-video-dialog"
									/>
								</div>
							</div>
						)}
						<div>
							<h4 className="text-lg font-semibold text-gray-900">
								{name}
							</h4>
							<p className="text-gray-500">{company_name}</p>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
};
